import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import Logo from "@/components/brand/Logo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";

const COLS = [
  { title: "Services", links: [
    ["Website Development", "/services/website-development"],
    ["Web App Development", "/services/web-app-development"],
    ["Android Apps", "/services/android-app-development"],
    ["AI Automation", "/services/ai-automation"],
    ["SEO & Local SEO", "/services/seo-local-seo"],
  ]},
  { title: "Company", links: [
    ["About", "/about"], ["Portfolio", "/portfolio"], ["Case Studies", "/case-studies"],
    ["Careers", "/careers"], ["Contact", "/contact"],
  ]},
  { title: "Resources", links: [
    ["Blog", "/blog"], ["Pricing", "/pricing"], ["FAQ", "/faq"],
    ["Testimonials", "/testimonials"], ["Book a Call", "/consultation"],
  ]},
  { title: "Legal", links: [
    ["Privacy Policy", "/privacy"], ["Terms & Conditions", "/terms"], ["Cookie Policy", "/cookie-policy"],
    ["Refund Policy", "/refund-policy"], ["Disclaimer", "/disclaimer"],
  ]},
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email, consent: true });
      toast.success(data.message || "Subscribed!");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <footer className="relative mt-24 border-t border-border bg-card/40">
      <div className="bs-container py-16">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_2fr]">
          <div>
            <Logo />
            <p className="mt-4 max-w-sm text-sm text-muted-foreground">
              Buddy Solution is a premium, worldwide digital-solutions partner for websites, apps,
              AI automation, design and growth, built with real craft and transparency.
            </p>
            <div className="mt-6 space-y-2 text-sm text-muted-foreground">
              <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> hello@buddysolution.net</p>
              <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Worldwide / Remote-first</p>
              <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Mon-Fri, 9am-6pm</p>
            </div>
            <form onSubmit={subscribe} className="mt-6 flex max-w-sm gap-2" data-testid="newsletter-form">
              <Input type="email" required placeholder="Your email for updates" value={email}
                onChange={(e) => setEmail(e.target.value)} data-testid="newsletter-email" />
              <Button type="submit" disabled={busy} data-testid="newsletter-submit">
                <ArrowRight className="h-4 w-4" />
              </Button>
            </form>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
            {COLS.map((c) => (
              <div key={c.title}>
                <h4 className="font-display text-sm font-bold">{c.title}</h4>
                <ul className="mt-4 space-y-2.5">
                  {c.links.map(([label, to]) => (
                    <li key={to}>
                      <Link to={to} className="text-sm text-muted-foreground transition-colors hover:text-foreground">{label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-border pt-8 text-sm text-muted-foreground sm:flex-row">
          <p>© {new Date().getFullYear()} Buddy Solution. All rights reserved.</p>
          <p>Technology. Trust. Growth.</p>
        </div>
      </div>
    </footer>
  );
}
